import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Grid, Stack,
  FormControl, InputLabel, Select, MenuItem, TextField, TablePagination,
  InputAdornment
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  AccountBalance, CheckCircle, Pending, Visibility, Payments, ArrowForward, Receipt
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { updateScreenTitle } from '../../../LayoutContainers/AppLayout/redux/AppSlice';
import AppService from '../../../Configurations/Appservice';
import pxToRem from '../../../Themes/Functions/pxToRem';
import moment from 'moment';

// Status chip component - matching customer design
const StatusChip = ({ status }) => {
  let color = 'default';

  switch (status?.toUpperCase()) {
    case 'COMPLETED':
      color = 'success';
      break;
    case 'IN_PROGRESS':
      color = 'warning';
      break;
    case 'CANCELLED':
    case 'REJECTED':
      color = 'error';
      break;
    case 'CREATED':
    case 'ASSIGNED':
    case 'PAID':
      color = 'success';
      break;
    case 'PENDING':
    case 'UNPAID':
      color = 'warning';
      break;
    default:
      color = 'default';
  }

  return (
    <Chip
      label={status || 'Unknown'}
      color={color}
      size="small"
      sx={{ height: 24, fontSize: '0.75rem' }}
    />
  );
};

// Metric Tile Component - matching ServicePartnerPaymentPage design
const MetricTile = ({ title, amount, subtitle, icon: Icon, color, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      background: color,
      borderRadius: 2,
      p: 2.5,
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      minHeight: 130,
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: 6
      } : {}
    }}
  >
    {/* Icon in top right */}
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        right: 12,
        opacity: 0.15,
        color: 'white'
      }}
    >
      <Icon sx={{ fontSize: 42 }} />
    </Box>

    {/* Content */}
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: 'rgba(255, 255, 255, 0.95)',
          fontWeight: 500,
          mb: 1.5,
          fontSize: '0.75rem'
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="h4"
        sx={{
          color: '#fff',
          fontWeight: 700,
          mb: 0.75,
          fontSize: '1.4rem',
          lineHeight: 1.2
        }}
      >
        {amount}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255, 255, 255, 0.85)',
          fontSize: '0.65rem',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  </Card>
);

// Dual-Amount Tile Component - matching Image #2 Ticket Payments style (compact)
const DualAmountTile = ({ title, primaryAmount, primaryLabel, primaryCount, secondaryAmount, secondaryLabel, secondaryCount, icon: Icon, color }) => (
  <Card
    sx={{
      background: color,
      borderRadius: 2,
      p: 2.5,
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      minHeight: 130,
      maxHeight: 130
    }}
  >
    {/* Icon in top right */}
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        right: 12,
        opacity: 0.15,
        color: 'white'
      }}
    >
      <Icon sx={{ fontSize: 42 }} />
    </Box>

    {/* Content */}
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: 'rgba(255, 255, 255, 0.95)',
          fontWeight: 500,
          mb: 1,
          fontSize: '0.75rem'
        }}
      >
        {title}
      </Typography>

      {/* Primary Amount Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
        <Typography
          variant="h5"
          sx={{
            color: '#fff',
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: 1.2
          }}
        >
          {primaryAmount}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.95rem'
          }}
        >
          {primaryCount}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '0.6rem',
          display: 'block',
          mb: 0.5
        }}
      >
        {primaryLabel}
      </Typography>

      {/* Secondary Amount Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
        <Typography
          variant="h5"
          sx={{
            color: secondaryLabel.includes('Unpaid') || secondaryLabel.includes('Pending') ? '#fb923c' : '#86efac',
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: 1.2
          }}
        >
          {secondaryAmount}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: secondaryLabel.includes('Unpaid') || secondaryLabel.includes('Pending') ? '#fb923c' : '#86efac',
            fontWeight: 600,
            fontSize: '0.95rem'
          }}
        >
          {secondaryCount}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '0.6rem',
          display: 'block'
        }}
      >
        {secondaryLabel}
      </Typography>
    </Box>
  </Card>
);

// Date Range Filter buttons
const DateRangeButtons = ({ selected, onSelect }) => {
  const ranges = [
    { label: '24HR', days: 1 },
    { label: '7 DAYS', days: 7 },
    { label: '30 DAYS', days: 30 },
    { label: '90 DAYS', days: 90 },
    { label: 'ALL', days: 0 },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {ranges.map(r => (
        <Button key={r.label} size="small" variant={selected === r.days ? 'contained' : 'outlined'}
          onClick={() => onSelect(r.days)}
          sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 0,
            ...(selected === r.days ? { bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } } : { color: '#2e7d32', borderColor: '#2e7d32' })
          }}>{r.label}</Button>
      ))}
    </Box>
  );
};

// Transaction History Tab — with search, pagination, date range, compact font
const TransactionHistoryTab = ({ paymentHistory, loading, filterTransactionStatus, setFilterTransactionStatus, formatDate, getStatusChip, handleViewDetails }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(0); // 0 = all

  const filteredPayments = paymentHistory.filter(payment => {
    const statusUpper = payment.status?.toUpperCase() || '';
    const matchesStatus = filterTransactionStatus === 'all' ||
      (filterTransactionStatus === 'APPROVED' ? (statusUpper === 'APPROVED' || statusUpper === 'CONFIRMED') : statusUpper === filterTransactionStatus.toUpperCase());
    const matchesSearch = !search ||
      (payment.referenceNumber || '').toLowerCase().includes(search.toLowerCase()) ||
      (payment.description || '').toLowerCase().includes(search.toLowerCase());
    let matchesDate = true;
    if (dateRange > 0) {
      const payDate = new Date(payment.createdAt || payment.initiatedAt);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - dateRange);
      matchesDate = payDate >= cutoff;
    }
    return matchesStatus && matchesSearch && matchesDate;
  });

  const paginatedPayments = filteredPayments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const fs = '0.75rem';

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2 }}>
      {/* Date Range */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">Date Range</Typography>
        <DateRangeButtons selected={dateRange} onSelect={(d) => { setDateRange(d); setPage(0); }} />
      </Box>

      {/* Search + Status Filter */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
        <TextField size="small" placeholder="Search transactions..." value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
          sx={{ minWidth: 220, '& input': { fontSize: fs } }} />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ fontSize: fs }}>Status</InputLabel>
          <Select value={filterTransactionStatus} label="Status" onChange={(e) => { setFilterTransactionStatus(e.target.value); setPage(0); }} sx={{ fontSize: fs }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="APPROVED">Received</MenuItem>
            <MenuItem value="PENDING_APPROVAL">Pending</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
          </Select>
        </FormControl>
        {(filterTransactionStatus !== 'all' || search || dateRange > 0) && (
          <Button size="small" onClick={() => { setFilterTransactionStatus('all'); setSearch(''); setDateRange(0); setPage(0); }}
            sx={{ fontSize: fs }}>Clear</Button>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredPayments.length} transactions found
        </Typography>
      </Box>

      {filteredPayments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" sx={{ fontSize: fs }}>No transactions found</Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#16697A' }}>
                  {['Date', 'Transaction #', 'Service', 'From Bank', 'To Bank', 'Amount', 'Status'].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 600, py: 1 }}
                      align={h === 'Amount' ? 'right' : 'left'}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPayments.map((payment) => {
                  const ticketCount = payment.tickets?.length || 0;
                  return (
                    <TableRow key={payment.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleViewDetails(payment, true)}>
                      <TableCell sx={{ fontSize: fs, py: 0.75 }}>{formatDate(payment.createdAt || payment.initiatedAt)}</TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Typography sx={{ fontSize: fs, fontWeight: 600, color: 'primary.main' }}>{payment.referenceNumber || '-'}</Typography>
                        {ticketCount > 0 && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{ticketCount} ticket{ticketCount > 1 ? 's' : ''}</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: fs, py: 0.75 }}>{payment.description || '-'}</TableCell>
                      <TableCell sx={{ fontSize: fs, py: 0.75 }}>{payment.sourceBankAccountName || '-'}</TableCell>
                      <TableCell sx={{ fontSize: fs, py: 0.75 }}>{payment.partnerBankAccountName || '-'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: fs, fontWeight: 600, py: 0.75 }}>AED {(payment.amount || 0).toFixed(2)}</TableCell>
                      <TableCell sx={{ py: 0.75 }}>{getStatusChip(payment.status)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filteredPayments.length} page={page}
            onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{ '& .MuiTablePagination-displayedRows': { fontSize: fs }, '& .MuiTablePagination-selectLabel': { fontSize: fs } }} />
        </>
      )}
    </Box>
  );
};

// Ticket History Tab — server-paginated against /v2/ticket/partner.
// That endpoint returns Thaklis→partner paymentStatus (PartnerPaymentStatus enum
// values) from the linked partner-payment transaction, so the Payment Status
// column reflects the right semantics for this dashboard.
const TicketHistoryTab = ({ active, formatDate }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(0);
  const fs = '0.75rem';

  // Server pagination. Date range stays client-side as before — the v2 endpoint
  // doesn't accept a date filter and we're working on one page at a time anyway.
  const fetchTickets = async () => {
    if (!active) return;
    setLoading(true);
    try {
      const resp = await AppService.get('v2/ticket/partner', {
        params: { page, pageSize: rowsPerPage }
      });
      const list = Array.isArray(resp) ? resp : (resp?.data || resp?.content || []);
      setTickets(list);
    } catch (err) {
      console.error('Error fetching partner tickets:', err);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, page, rowsPerPage]);

  // Client-side filtering for status / paymentStatus / search / date — operates
  // on the current page only, matching the previous tab's behaviour.
  const uniqueStatuses = [...new Set(tickets.map(t => t.status?.toUpperCase()).filter(Boolean))];
  const filteredTickets = tickets.filter(ticket => {
    const status = ticket.status || '';
    const paymentStatus = ticket.paymentStatus || '';
    const matchesStatus = filterStatus === 'all' || status.toUpperCase() === filterStatus.toUpperCase();
    const matchesPayment = filterPaymentStatus === 'all' || paymentStatus.toUpperCase() === filterPaymentStatus.toUpperCase();
    const matchesSearch = !search ||
      (ticket.orgTicketNumber || '').toLowerCase().includes(search.toLowerCase()) ||
      (ticket.customerName || '').toLowerCase().includes(search.toLowerCase()) ||
      (ticket.serviceName || '').toLowerCase().includes(search.toLowerCase()) ||
      String(ticket.ticketNumber || '').includes(search);
    let matchesDate = true;
    if (dateRange > 0) {
      const tDate = new Date(typeof ticket.createdDate === 'number' ? ticket.createdDate * 1000 : ticket.createdDate);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - dateRange);
      matchesDate = tDate >= cutoff;
    }
    return matchesStatus && matchesPayment && matchesSearch && matchesDate;
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2 }}>
      {/* Date Range */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">Date Range</Typography>
        <DateRangeButtons selected={dateRange} onSelect={(d) => setDateRange(d)} />
      </Box>

      {/* Search + Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search tickets..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
          sx={{ minWidth: 220, '& input': { fontSize: fs } }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ fontSize: fs }}>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)} sx={{ fontSize: fs }}>
            <MenuItem value="all">All Statuses</MenuItem>
            {uniqueStatuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel sx={{ fontSize: fs }}>Payment Status</InputLabel>
          <Select value={filterPaymentStatus} label="Payment Status" onChange={(e) => setFilterPaymentStatus(e.target.value)} sx={{ fontSize: fs }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="UNPAID">UNPAID</MenuItem>
            <MenuItem value="PENDING_APPROVAL">PENDING APPROVAL</MenuItem>
            <MenuItem value="APPROVED">APPROVED</MenuItem>
            <MenuItem value="TRANSFER_INITIATED">TRANSFER INITIATED</MenuItem>
            <MenuItem value="CONFIRMED">CONFIRMED</MenuItem>
            <MenuItem value="REJECTED">REJECTED</MenuItem>
          </Select>
        </FormControl>
        {(filterStatus !== 'all' || filterPaymentStatus !== 'all' || search || dateRange > 0) && (
          <Button size="small" onClick={() => { setFilterStatus('all'); setFilterPaymentStatus('all'); setSearch(''); setDateRange(0); }}
            sx={{ fontSize: fs }}>Clear</Button>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredTickets.length} on this page
        </Typography>
      </Box>

      {/* Table — original column layout */}
      {filteredTickets.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" sx={{ fontSize: fs }}>No tickets found</Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#16697A' }}>
                  {['Ticket #', 'Customer', 'Service', 'Date', 'Txn Ref', 'Status', 'Payment', 'Amount', 'Gov Fees', 'Partner Fee'].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 600, py: 1 }}
                      align={['Amount', 'Gov Fees', 'Partner Fee'].includes(h) ? 'right' : 'left'}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTickets.map((ticket) => (
                  <TableRow key={ticket.id} hover>
                    <TableCell sx={{ fontSize: fs, fontWeight: 600, color: 'primary.main', py: 0.75 }}>
                      {ticket.orgTicketNumber || `#${ticket.ticketNumber}` || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: fs, py: 0.75 }}>{ticket.customerName || '-'}</TableCell>
                    <TableCell sx={{ fontSize: fs, py: 0.75 }}>{ticket.serviceName || '-'}</TableCell>
                    <TableCell sx={{ fontSize: fs, py: 0.75 }}>{formatDate(ticket.createdDate)}</TableCell>
                    <TableCell sx={{ fontSize: '0.65rem', py: 0.75, color: 'primary.main' }}>
                      {ticket.transactionReference || '-'}
                    </TableCell>
                    <TableCell sx={{ py: 0.75 }}>
                      <StatusChip status={ticket.status} />
                    </TableCell>
                    <TableCell sx={{ py: 0.75 }}>
                      <StatusChip status={ticket.paymentStatus || 'UNPAID'} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: fs, fontWeight: 600, py: 0.75 }}>
                      AED {(ticket.amount || ticket.totalAmount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: fs, py: 0.75, color: 'text.secondary' }}>
                      {ticket.governmentFees ? `AED ${ticket.governmentFees.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: fs, py: 0.75, color: 'text.secondary' }}>
                      {ticket.partnerServiceFee ? `AED ${ticket.partnerServiceFee.toFixed(2)}` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={-1}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelDisplayedRows={({ from, to }) => `${from}–${to}`}
            sx={{ '& .MuiTablePagination-displayedRows': { fontSize: fs }, '& .MuiTablePagination-selectLabel': { fontSize: fs } }}
          />
        </>
      )}
    </Box>
  );
};

const PartnerPaymentDashboard = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const [filterTransactionStatus, setFilterTransactionStatus] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [error, setError] = useState(null);

  // Summary data
  const [summary, setSummary] = useState({
    paid:       { amount: 0, count: 0 },
    inReview:   { amount: 0, count: 0 },
    inProgress: { amount: 0, count: 0 }
  });

  // Bank account data
  const [bankAccount, setBankAccount] = useState(null);

  // Click handlers for metric tiles
  const handleTotalReceivedClick = () => {
    setActiveTab(1); // Switch to Transaction History tab
    setFilterTransactionStatus('APPROVED'); // Filter by Approved (received) status
  };

  const handleInProgressClick = () => {
    setActiveTab(2); // Switch to Ticket History tab
    setFilterPaymentStatus('UNPAID'); // Filter by UNPAID payment status
    setFilterStatus('all'); // Reset ticket status filter
  };

  const handlePendingConfirmationClick = () => {
    setActiveTab(0); // Switch to Pending Confirmation tab
    setFilterPaymentStatus('all'); // Reset payment status filter
    setFilterStatus('all'); // Reset ticket status filter
  };

  useEffect(() => {
    dispatch(updateScreenTitle('Payment Dashboard'));
    return () => dispatch(updateScreenTitle(''));
  }, [dispatch]);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      // Get organization ID from localStorage
      const authInfo = localStorage.getItem('authInfo');
      let organizationId = null;
      if (authInfo) {
        const parsed = JSON.parse(authInfo);
        organizationId = parsed?.principalInfo?.organisationId;
      }

      // Fetch pending, history, summary (replaces wholesale /v2/ticket/partner),
      // and bank account in parallel. Ticket History tab now loads its own
      // paginated data via /v1/ticket/allWithWorkflowInfo.
      const [pendingResponse, historyResponse, summaryResponse, bankAccountResponse] = await Promise.all([
        AppService.get('v2/partner-payment-transactions/partner/pending-approval').catch(err => {
          console.error('Error fetching pending approvals:', err);
          return [];
        }),
        organizationId
          ? AppService.get(`v2/partner-payment-transactions/partner/${organizationId}`).catch(err => {
              console.error('Error fetching payment history:', err);
              return [];
            })
          : Promise.resolve([]),
        AppService.get('v2/ticket/partner/summary').catch(err => {
          console.error('Error fetching dashboard summary:', err);
          return null;
        }),
        organizationId
          ? AppService.get(`v1/partner-bank-accounts/partner/${organizationId}/primary`).catch(() => null)
          : Promise.resolve(null)
      ]);

      const pendingData = pendingResponse?.data || pendingResponse;
      const historyData = historyResponse?.data || historyResponse;

      const pending = Array.isArray(pendingData) ? pendingData : [];
      const history = Array.isArray(historyData) ? historyData : [];

      setPendingPayments(pending);
      setPaymentHistory(history);
      setBankAccount(bankAccountResponse);

      // Tile values come straight from the lightweight summary endpoint.
      // Bucket definitions are mirrored from Finance Overview's service-partner section:
      //   Paid       — transactions in APPROVED/TRANSFER_INITIATED/CONFIRMED
      //   In Review  — transactions in PENDING_APPROVAL
      //   In Progress— COMPLETED tickets with no live partner-payment transaction
      const sum = summaryResponse || {};
      setSummary({
        paid:       { amount: sum.paid?.amount       || 0, count: sum.paid?.count       || 0 },
        inReview:   { amount: sum.inReview?.amount   || 0, count: sum.inReview?.count   || 0 },
        inProgress: { amount: sum.inProgress?.amount || 0, count: sum.inProgress?.count || 0 }
      });

      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (payment, viewOnly = false) => {
    setSelectedPayment(payment);
    setIsViewOnly(viewOnly);
    setShowRejectInput(false);
    setRejectReason('');
    setDetailsOpen(true);
  };

  const handleConfirmPayment = async (payment) => {
    setConfirming(true);
    try {
      const transactionId = payment.id;
      await AppService.put(`v2/partner-payment-transactions/${transactionId}/partner-approve`);
      setDetailsOpen(false);
      fetchPayments();
    } catch (err) {
      console.error('Error confirming payment:', err);
      setError('Failed to confirm payment');
    } finally {
      setConfirming(false);
    }
  };

  const handleRejectPayment = async (payment) => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    setRejecting(true);
    try {
      const transactionId = payment.id;
      await AppService.put(`v2/partner-payment-transactions/${transactionId}/partner-reject`, null, {
        params: { reason: rejectReason }
      });
      setDetailsOpen(false);
      setRejectReason('');
      setShowRejectInput(false);
      fetchPayments();
    } catch (err) {
      console.error('Error rejecting payment:', err);
      setError('Failed to reject payment');
    } finally {
      setRejecting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Handle Unix timestamp (epoch seconds) - convert to milliseconds
    if (typeof dateStr === 'number') {
      return moment(dateStr * 1000).format('YYYY-MM-DD');
    }
    return moment(dateStr).format('YYYY-MM-DD');
  };

  const formatCurrency = (amount) => {
    return `AED ${(amount || 0).toFixed(2)}`;
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      'TRANSFER_INITIATED': { label: 'Processing', color: 'warning' },
      'CONFIRMED': { label: 'Received', color: 'success' },
      'PAID': { label: 'Received', color: 'success' },
      'PENDING_APPROVAL': { label: 'Pending', color: 'warning' },
      'APPROVED': { label: 'Received', color: 'success' },
      'UNPAID': { label: 'Pending', color: 'warning' },
      'REJECTED': { label: 'Rejected', color: 'error' }
    };
    const config = statusConfig[status] || { label: 'Unpaid', color: 'error' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  // Payment Item Component - matching ServicePartnerPaymentPage design
  // Supports both v2 API response format and legacy format
  const PaymentItem = ({ item }) => {
    // Get ticket count from tickets array or ticketCount field
    const ticketCount = item.tickets?.length || item.ticketCount || 0;
    
    // Get service name from description, first ticket, or serviceName field
    const serviceName = item.description || 
      (item.tickets?.[0]?.serviceName) || 
      item.serviceName || 
      '-';

    return (
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, overflow: 'hidden' }}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          sx={{
            py: 1.5,
            px: 2,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
          onClick={() => handleViewDetails(item, false)}
        >
          {/* Date - use createdAt (v2) or createdDate (legacy) */}
          <Box sx={{ width: '12%' }}>
            <Typography fontSize={13} fontWeight={500}>
              {formatDate(item.createdAt || item.createdDate)}
            </Typography>
          </Box>
          {/* Transaction # - use referenceNumber (v2) or transactionNumber (legacy) */}
          <Box sx={{ width: '20%' }}>
            <Typography fontSize={14} fontWeight={600} color="primary.main">
              {item.referenceNumber || item.transactionNumber || item.ticketNumber || '-'}
            </Typography>
            {ticketCount > 0 && (
              <Typography fontSize={11} color="text.secondary">
                {ticketCount} ticket{ticketCount > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
          {/* Service */}
          <Box sx={{ width: '18%' }}>
            <Typography fontSize={12} color="text.secondary" noWrap>
              {serviceName}
            </Typography>
          </Box>
          {/* From Bank - use sourceBankAccountName (v2) or sourceBankName (legacy) */}
          <Box sx={{ width: '15%' }}>
            <Typography fontSize={12} color="text.secondary" noWrap>
              {item.sourceBankAccountName || item.sourceBankName || '-'}
            </Typography>
          </Box>
          {/* To Bank - use partnerBankAccountName (v2) or destBankName (legacy) */}
          <Box sx={{ width: '15%' }}>
            <Typography fontSize={12} color="text.secondary" noWrap>
              {item.partnerBankAccountName || item.destBankName || '-'}
            </Typography>
          </Box>
          {/* Amount */}
          <Box sx={{ width: '13%', textAlign: 'right' }}>
            <Typography fontSize={14} fontWeight={600} color="primary.main">
              {formatCurrency(item.amount)}
            </Typography>
          </Box>
          {/* Status */}
          <Box sx={{ width: '12%', textAlign: 'right' }}>
            {getStatusChip(item.status)}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${pxToRem(55)})`,
        width: '100%',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Metric Tiles */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          {/* Paid — money Thaklis has paid to the partner */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricTile
              title="Paid"
              amount={formatCurrency(summary.paid.amount)}
              subtitle={`${summary.paid.count} ticket${summary.paid.count !== 1 ? 's' : ''}`}
              icon={CheckCircle}
              color="#1a7f8f"
              onClick={handleTotalReceivedClick}
            />
          </Grid>

          {/* In Review — initiated, awaiting partner approval */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricTile
              title="In Review"
              amount={formatCurrency(summary.inReview.amount)}
              subtitle={`${summary.inReview.count} ticket${summary.inReview.count !== 1 ? 's' : ''}`}
              icon={Pending}
              color="#03a9f4"
              onClick={handlePendingConfirmationClick}
            />
          </Grid>

          {/* In Progress — completed tickets with no payment initiated */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricTile
              title="In Progress"
              amount={formatCurrency(summary.inProgress.amount)}
              subtitle={`${summary.inProgress.count} ticket${summary.inProgress.count !== 1 ? 's' : ''}`}
              icon={Receipt}
              color="#1f6b7a"
              onClick={handleInProgressClick}
            />
          </Grid>

          {/* Bank Account */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricTile
              title="Bank Account"
              amount={bankAccount ? bankAccount.bankName || 'Active' : 'Not Set'}
              subtitle={
                bankAccount
                  ? `${bankAccount.accountHolderName || ''}\n****${bankAccount.accountNumber?.slice(-4) || ''}`
                  : 'Configure bank account'
              }
              icon={AccountBalance}
              color="#1f6b7a"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Tabs Section */}
      <Box sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ px: 2 }}
              >
                <Tab label="PENDING CONFIRMATION" />
                <Tab label="TRANSACTION HISTORY" />
                <Tab label="TICKET HISTORY" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {/* Tab 0: Pending Confirmation */}
              {activeTab === 0 && (() => {
                // Source of truth is pendingPayments from /v2/partner-payment-transactions/partner/pending-approval.
                // (Previously this also tried to reconstruct transactions from ticketHistory; that path
                // was dead code — its results were always overridden by pendingPayments anyway — and is gone.)
                const allPendingItems = [...pendingPayments];

                return (
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Payments Awaiting Your Confirmation</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Review and confirm payments you have received from Thaklis
                    </Typography>

                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <CircularProgress />
                      </Box>
                    ) : allPendingItems.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                        <Typography color="text.secondary">
                          No pending payments to confirm
                        </Typography>
                      </Box>
                    ) : (
                    <>
                      {/* Table Header */}
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        sx={{ py: 1, px: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}
                      >
                        <Box sx={{ width: '12%' }}>
                          <Typography fontSize={12} fontWeight={600}>Date</Typography>
                        </Box>
                        <Box sx={{ width: '20%' }}>
                          <Typography fontSize={12} fontWeight={600}>Transaction #</Typography>
                        </Box>
                        <Box sx={{ width: '18%' }}>
                          <Typography fontSize={12} fontWeight={600}>Service</Typography>
                        </Box>
                        <Box sx={{ width: '15%' }}>
                          <Typography fontSize={12} fontWeight={600}>From Bank</Typography>
                        </Box>
                        <Box sx={{ width: '15%' }}>
                          <Typography fontSize={12} fontWeight={600}>To Bank</Typography>
                        </Box>
                        <Box sx={{ width: '13%', textAlign: 'right' }}>
                          <Typography fontSize={12} fontWeight={600}>Amount</Typography>
                        </Box>
                        <Box sx={{ width: '7%', textAlign: 'right' }}>
                          <Typography fontSize={12} fontWeight={600}>Status</Typography>
                        </Box>
                      </Box>

                      {/* Payment List */}
                      <Stack sx={{ overflow: 'auto', flexGrow: 1 }}>
                        {allPendingItems.map((payment) => (
                          <PaymentItem key={payment.id} item={payment} />
                        ))}
                      </Stack>
                    </>
                  )}
                </Box>
              )})()}

              {/* Tab 1: Transaction History */}
              {activeTab === 1 && (
                <TransactionHistoryTab
                  paymentHistory={paymentHistory}
                  loading={loading}
                  filterTransactionStatus={filterTransactionStatus}
                  setFilterTransactionStatus={setFilterTransactionStatus}
                  formatDate={formatDate}
                  getStatusChip={getStatusChip}
                  handleViewDetails={handleViewDetails}
                />
              )}

              {/* Tab 2: Ticket History */}
              {activeTab === 2 && (
                <TicketHistoryTab active={activeTab === 2} formatDate={formatDate} />
              )}
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Payment Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={(_, reason) => {
          if (confirming || rejecting) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setDetailsOpen(false);
        }}
        disableEscapeKeyDown={confirming || rejecting}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#064d51', color: 'white', fontSize: '0.9rem', py: 1.25, px: 2, fontWeight: 600 }}>
          Payment Details
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 2 }}>
          {selectedPayment && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Reference Number</Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {selectedPayment.referenceNumber || selectedPayment.ticketNumber || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Amount</Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#064d51' }}>
                    {formatCurrency(selectedPayment.amount)}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Description</Typography>
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    {selectedPayment.description || selectedPayment.serviceName || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Date</Typography>
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    {formatDate(selectedPayment.createdAt || selectedPayment.createdDate)}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>From Bank</Typography>
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    {selectedPayment.sourceBankAccountName || selectedPayment.fromBank || selectedPayment.sourceBankName || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>To Bank</Typography>
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    {selectedPayment.partnerBankAccountName || selectedPayment.toBank || selectedPayment.destBankName || '-'}
                  </Typography>
                </Box>
                {selectedPayment.tickets && selectedPayment.tickets.length > 0 && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Tickets ({selectedPayment.tickets.length})</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {selectedPayment.tickets.map((ticket, idx) => (
                        <Typography key={idx} sx={{ fontSize: '0.75rem', mb: 0.25 }}>
                          #{ticket.orgTicketNumber || ticket.ticketNumber} - {ticket.serviceName} ({formatCurrency(ticket.ticketAmount)})
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {!isViewOnly && (
                <Alert severity="info" sx={{ fontSize: '0.7rem', py: 0.5 }}>
                  By confirming, you acknowledge that you have received this payment in your bank account.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2 }}>
          {!isViewOnly && showRejectInput && (
            <Box sx={{ width: '100%', mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Rejection Reason *"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                multiline
                rows={2}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
            <Button
              onClick={() => { setDetailsOpen(false); setShowRejectInput(false); setRejectReason(''); }}
              disabled={confirming || rejecting}
              variant="contained"
              size="small"
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#9e9e9e', '&:hover': { bgcolor: '#757575' } }}
            >
              Close
            </Button>
            {!isViewOnly && (
              <>
                {showRejectInput ? (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => handleRejectPayment(selectedPayment)}
                    disabled={rejecting || !rejectReason.trim()}
                  >
                    {rejecting ? <CircularProgress size={16} /> : 'Confirm Reject'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => setShowRejectInput(true)}
                  >
                    Reject
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CheckCircle />}
                  sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#064d51', '&:hover': { bgcolor: '#043a3e' } }}
                  onClick={() => handleConfirmPayment(selectedPayment)}
                  disabled={confirming}
                >
                  {confirming ? <CircularProgress size={16} /> : 'Approve'}
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
        {(confirming || rejecting) && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              zIndex: 1,
            }}
          >
            <CircularProgress />
            <Typography sx={{ fontSize: '0.9rem', color: '#444', fontWeight: 500 }}>
              {confirming ? 'Approving payment… please wait' : 'Rejecting payment… please wait'}
            </Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default PartnerPaymentDashboard;
